import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Activity,
    ArrowLeft,
    BarChart3,
    BookOpen,
    BriefcaseBusiness,
    CalendarDays,
    CheckCircle2,
    ClipboardList,
    Clock3,
    FlaskConical,
    LogOut,
    MessageSquareText,
    PhoneCall,
    RefreshCw,
    TriangleAlert,
} from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import Logo from '@/components/Logo';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { clearAdminCredentials, getAdminAuthHeader } from '@/lib/adminAuth';
import { getApiBase } from '@/lib/apiBase';
import { formatPersianDateTime, getTehranDateKey, parseTehranDate } from '@/utils/date-utils';

type DashboardItem = {
    id?: string;
    createdAt?: string;
    fullName?: string;
    name?: string;
    phone?: string;
    type?: string;
    quickRating?: string;
    sourceLabel?: string;
};

type DashboardData = {
    feedback: DashboardItem[];
    checkups: DashboardItem[];
    sampling: DashboardItem[];
    careers: DashboardItem[];
    contacts: DashboardItem[];
};

type SectionKey = keyof DashboardData;

type SectionConfig = {
    key: SectionKey | 'blog';
    label: string;
    description: string;
    endpoint?: string;
    path: string;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
    softColor: string;
};

const EMPTY_DATA: DashboardData = {
    feedback: [],
    checkups: [],
    sampling: [],
    careers: [],
    contacts: [],
};

const SECTION_CONFIG: SectionConfig[] = [
    {
        key: 'feedback',
        label: 'نظرسنجی‌ها',
        description: 'بازخورد و امتیاز مراجعین',
        endpoint: '/api/feedback-list.php',
        path: '/admin/feedback',
        icon: MessageSquareText,
        color: '#2563eb',
        softColor: 'bg-blue-50 text-blue-700 border-blue-100',
    },
    {
        key: 'checkups',
        label: 'چکاپ‌ها',
        description: 'درخواست‌های رزرو چکاپ',
        endpoint: '/api/checkup-list.php',
        path: '/admin/checkups',
        icon: ClipboardList,
        color: '#059669',
        softColor: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    },
    {
        key: 'sampling',
        label: 'نمونه‌گیری',
        description: 'درخواست‌های نمونه‌گیری در محل',
        endpoint: '/api/sampling-list.php',
        path: '/admin/sampling',
        icon: FlaskConical,
        color: '#7c3aed',
        softColor: 'bg-violet-50 text-violet-700 border-violet-100',
    },
    {
        key: 'careers',
        label: 'رزومه‌ها',
        description: 'درخواست‌های همکاری',
        endpoint: '/api/careers-list.php',
        path: '/admin/careers',
        icon: BriefcaseBusiness,
        color: '#d97706',
        softColor: 'bg-amber-50 text-amber-700 border-amber-100',
    },
    {
        key: 'contacts',
        label: 'پیام‌ها',
        description: 'پیام‌های فرم تماس',
        endpoint: '/api/contacts-list.php',
        path: '/admin/contacts',
        icon: PhoneCall,
        color: '#e11d48',
        softColor: 'bg-rose-50 text-rose-700 border-rose-100',
    },
    {
        key: 'blog',
        label: 'بلاگ',
        description: 'مدیریت مطالب سایت',
        path: '/admin/blog',
        icon: BookOpen,
        color: '#0f766e',
        softColor: 'bg-teal-50 text-teal-700 border-teal-100',
    },
];

const AdminDashboard: React.FC = () => {
    const [data, setData] = useState<DashboardData>(EMPTY_DATA);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [failedSections, setFailedSections] = useState<string[]>([]);
    const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
    const navigate = useNavigate();

    const fetchDashboard = useCallback(async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);
        setFailedSections([]);

        const auth = getAdminAuthHeader();
        if (!auth) {
            setLoading(false);
            setRefreshing(false);
            navigate('/admin/login', { replace: true });
            return;
        }

        const dataSections = SECTION_CONFIG.filter((section): section is SectionConfig & { key: SectionKey; endpoint: string } => Boolean(section.endpoint));

        try {
            const results = await Promise.allSettled(
                dataSections.map(async (section) => {
                    const response = await fetch(`${getApiBase()}${section.endpoint}`, {
                        headers: { Authorization: auth },
                    });
                    if (response.status === 401) throw new Error('unauthorized');
                    if (!response.ok) throw new Error(section.label);
                    const json = await response.json();
                    return {
                        key: section.key,
                        label: section.label,
                        items: Array.isArray(json.items) ? json.items : [],
                    };
                }),
            );

            if (results.some(result => result.status === 'rejected' && result.reason?.message === 'unauthorized')) {
                clearAdminCredentials();
                navigate('/admin/login', { replace: true });
                return;
            }

            const nextData = { ...EMPTY_DATA };
            const failed: string[] = [];

            results.forEach((result, index) => {
                if (result.status === 'fulfilled') {
                    nextData[result.value.key] = result.value.items;
                } else {
                    failed.push(dataSections[index].label);
                }
            });

            setData(nextData);
            setFailedSections(failed);
            setLastRefresh(new Date());
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [navigate]);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    const allItems = useMemo(
        () => Object.entries(data).flatMap(([section, items]) => items.map(item => ({ ...item, section: section as SectionKey }))),
        [data],
    );

    const todayKey = getTehranDateKey(new Date());
    const sevenDaysAgoKey = getTehranDateKey(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000));
    const thirtyDaysAgoKey = getTehranDateKey(new Date(Date.now() - 29 * 24 * 60 * 60 * 1000));
    const itemDateKey = (item: DashboardItem) => {
        const parsed = parseTehranDate(item.createdAt);
        return parsed ? getTehranDateKey(parsed) : '';
    };

    const stats = useMemo(() => ({
        total: allItems.length,
        today: allItems.filter(item => itemDateKey(item) === todayKey).length,
        week: allItems.filter(item => itemDateKey(item) >= sevenDaysAgoKey && itemDateKey(item) <= todayKey).length,
        month: allItems.filter(item => itemDateKey(item) >= thirtyDaysAgoKey && itemDateKey(item) <= todayKey).length,
    }), [allItems, thirtyDaysAgoKey, todayKey, sevenDaysAgoKey]);

    const activityData = useMemo(() => Array.from({ length: 7 }, (_, index) => {
        const date = new Date(Date.now() - (6 - index) * 24 * 60 * 60 * 1000);
        const dateKey = getTehranDateKey(date);
        return {
            day: date.toLocaleDateString('fa-IR', { weekday: 'short', timeZone: 'Asia/Tehran' }),
            count: allItems.filter(item => itemDateKey(item) === dateKey).length,
        };
    }), [allItems]);

    const serviceData = useMemo(
        () => SECTION_CONFIG
            .filter((section): section is SectionConfig & { key: SectionKey } => section.key !== 'blog')
            .map(section => ({
                name: section.label,
                value: data[section.key].length,
                color: section.color,
            })),
        [data],
    );

    const recentItems = useMemo(
        () => [...allItems]
            .sort((first, second) => (parseTehranDate(second.createdAt)?.getTime() || 0) - (parseTehranDate(first.createdAt)?.getTime() || 0))
            .slice(0, 8),
        [allItems],
    );

    const handleLogout = () => {
        clearAdminCredentials();
        navigate('/admin/login', { replace: true });
    };

    const getSection = (key: SectionKey) => SECTION_CONFIG.find(section => section.key === key)!;

    return (
        <div dir="rtl" className="min-h-screen bg-slate-50 text-slate-900">
            <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
                <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-3 px-4 py-3 sm:px-6">
                    <div className="flex min-w-0 items-center gap-3">
                        <div className="rounded-xl border border-slate-200 bg-white p-1.5 shadow-sm">
                            <Logo size="sm" />
                        </div>
                        <div className="min-w-0">
                            <h1 className="truncate text-base font-bold sm:text-xl">داشبورد مدیریت آزمایشگاه</h1>
                            <p className="hidden text-xs text-slate-500 sm:block">نمای کلی درخواست‌ها و فعالیت‌های سایت</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => fetchDashboard(true)}
                            disabled={refreshing || loading}
                            aria-label="بروزرسانی داشبورد"
                        >
                            <RefreshCw className={`h-4 w-4 sm:ml-2 ${refreshing ? 'animate-spin' : ''}`} />
                            <span className="hidden sm:inline">بروزرسانی</span>
                        </Button>
                        <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-600 hover:bg-red-50 hover:text-red-600">
                            <LogOut className="h-4 w-4 sm:ml-2" />
                            <span className="hidden sm:inline">خروج</span>
                        </Button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-[1500px] space-y-6 px-4 py-5 sm:px-6 sm:py-7">
                <section className="flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
                    <div>
                        <p className="text-sm font-medium text-blue-700">امروز {new Date().toLocaleDateString('fa-IR', { dateStyle: 'full', timeZone: 'Asia/Tehran' })}</p>
                        <h2 className="mt-1 text-2xl font-extrabold tracking-tight sm:text-3xl">سلام، وضعیت امروز آزمایشگاه اینجاست</h2>
                    </div>
                    {lastRefresh && (
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                            آخرین بروزرسانی {lastRefresh.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tehran' })}
                        </div>
                    )}
                </section>

                {failedSections.length > 0 && (
                    <Alert className="border-amber-200 bg-amber-50">
                        <TriangleAlert className="h-4 w-4 text-amber-700" />
                        <AlertDescription className="flex flex-col justify-between gap-3 text-amber-900 sm:flex-row sm:items-center">
                            <span>دریافت اطلاعات این بخش‌ها ناموفق بود: {failedSections.join('، ')}</span>
                            <Button variant="outline" size="sm" onClick={() => fetchDashboard(true)} className="border-amber-300 bg-white">
                                تلاش مجدد
                            </Button>
                        </AlertDescription>
                    </Alert>
                )}

                <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                    {loading ? Array.from({ length: 4 }).map((_, index) => (
                        <Card key={index} className="border-slate-200 shadow-sm">
                            <CardContent className="p-4 sm:p-5">
                                <Skeleton className="mb-4 h-10 w-10 rounded-xl" />
                                <Skeleton className="mb-2 h-7 w-16" />
                                <Skeleton className="h-4 w-24" />
                            </CardContent>
                        </Card>
                    )) : [
                        { label: 'امروز', value: stats.today, icon: CalendarDays, color: 'bg-blue-50 text-blue-700' },
                        { label: '۷ روز اخیر', value: stats.week, icon: Activity, color: 'bg-emerald-50 text-emerald-700' },
                        { label: '۳۰ روز اخیر', value: stats.month, icon: BarChart3, color: 'bg-violet-50 text-violet-700' },
                        { label: 'کل درخواست‌ها', value: stats.total, icon: ClipboardList, color: 'bg-amber-50 text-amber-700' },
                    ].map(stat => (
                        <Card key={stat.label} className="border-slate-200 shadow-sm transition-shadow hover:shadow-md">
                            <CardContent className="p-4 sm:p-5">
                                <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-xl ${stat.color}`}>
                                    <stat.icon className="h-5 w-5" />
                                </div>
                                <div className="text-2xl font-extrabold sm:text-3xl">{stat.value.toLocaleString('fa-IR')}</div>
                                <div className="mt-1 text-xs font-medium text-slate-500 sm:text-sm">{stat.label}</div>
                            </CardContent>
                        </Card>
                    ))}
                </section>

                <section>
                    <div className="mb-3 flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-bold">بخش‌های مدیریتی</h3>
                            <p className="text-sm text-slate-500">برای مشاهده و مدیریت هر بخش انتخاب کنید</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                        {SECTION_CONFIG.map(section => {
                            const count = section.key === 'blog' ? null : data[section.key].length;
                            const Icon = section.icon;
                            return (
                                <button
                                    key={section.key}
                                    type="button"
                                    onClick={() => navigate(section.path)}
                                    className="group flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 text-right shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                >
                                    <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border ${section.softColor}`}>
                                        <Icon className="h-6 w-6" />
                                    </span>
                                    <span className="min-w-0 flex-1">
                                        <span className="flex items-center gap-2">
                                            <span className="font-bold">{section.label}</span>
                                            {count !== null && <Badge variant="secondary">{count.toLocaleString('fa-IR')}</Badge>}
                                        </span>
                                        <span className="mt-1 block truncate text-sm text-slate-500">{section.description}</span>
                                    </span>
                                    <ArrowLeft className="h-5 w-5 shrink-0 text-slate-300 transition group-hover:-translate-x-1 group-hover:text-slate-600" />
                                </button>
                            );
                        })}
                    </div>
                </section>

                <section className="grid grid-cols-1 gap-5 xl:grid-cols-5">
                    <Card className="border-slate-200 shadow-sm xl:col-span-3">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <BarChart3 className="h-5 w-5 text-blue-600" />
                                فعالیت ۷ روز اخیر
                            </CardTitle>
                            <CardDescription>تعداد کل موارد ثبت‌شده در هر روز</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {loading ? <Skeleton className="h-72 w-full" /> : (
                                <div className="h-72 w-full" dir="ltr">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={activityData} margin={{ top: 10, right: 5, left: -20, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                            <XAxis dataKey="day" tick={{ fill: '#64748b', fontSize: 12 }} axisLine={false} tickLine={false} />
                                            <YAxis allowDecimals={false} tick={{ fill: '#64748b', fontSize: 12 }} axisLine={false} tickLine={false} />
                                            <Tooltip
                                                cursor={{ fill: '#eff6ff' }}
                                                contentStyle={{ direction: 'rtl', borderRadius: 10, borderColor: '#e2e8f0' }}
                                                formatter={(value: number) => [`${value.toLocaleString('fa-IR')} مورد`, 'تعداد']}
                                            />
                                            <Bar dataKey="count" fill="#2563eb" radius={[6, 6, 0, 0]} maxBarSize={42} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card className="border-slate-200 shadow-sm xl:col-span-2">
                        <CardHeader>
                            <CardTitle>توزیع بخش‌ها</CardTitle>
                            <CardDescription>سهم هر نوع درخواست از کل اطلاعات</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {loading ? <Skeleton className="h-72 w-full" /> : stats.total === 0 ? (
                                <div className="flex h-72 flex-col items-center justify-center text-center text-slate-500">
                                    <BarChart3 className="mb-3 h-10 w-10 text-slate-300" />
                                    هنوز داده‌ای ثبت نشده است
                                </div>
                            ) : (
                                <div className="grid min-h-72 grid-cols-1 items-center gap-4 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                                    <div className="h-52" dir="ltr">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie data={serviceData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} paddingAngle={2}>
                                                    {serviceData.map(entry => <Cell key={entry.name} fill={entry.color} />)}
                                                </Pie>
                                                <Tooltip
                                                    contentStyle={{ direction: 'rtl', borderRadius: 10, borderColor: '#e2e8f0' }}
                                                    formatter={(value: number) => [`${value.toLocaleString('fa-IR')} مورد`, 'تعداد']}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div className="space-y-2">
                                        {serviceData.map(entry => (
                                            <div key={entry.name} className="flex items-center gap-2 text-sm">
                                                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
                                                <span className="flex-1 text-slate-600">{entry.name}</span>
                                                <span className="font-bold">{entry.value.toLocaleString('fa-IR')}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </section>

                <Card className="border-slate-200 shadow-sm">
                    <CardHeader className="flex flex-row items-center justify-between gap-3">
                        <div>
                            <CardTitle className="flex items-center gap-2">
                                <Clock3 className="h-5 w-5 text-slate-600" />
                                آخرین فعالیت‌ها
                            </CardTitle>
                            <CardDescription>جدیدترین موارد ثبت‌شده در همه بخش‌ها</CardDescription>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="space-y-3">
                                {Array.from({ length: 5 }).map((_, index) => <Skeleton key={index} className="h-16 w-full" />)}
                            </div>
                        ) : recentItems.length === 0 ? (
                            <div className="py-12 text-center text-slate-500">هنوز فعالیتی ثبت نشده است.</div>
                        ) : (
                            <div className="divide-y divide-slate-100">
                                {recentItems.map((item, index) => {
                                    const section = getSection(item.section);
                                    const Icon = section.icon;
                                    const displayName = item.fullName || item.name || item.sourceLabel || 'بدون نام';
                                    return (
                                        <button
                                            key={`${item.section}-${item.id || index}`}
                                            type="button"
                                            onClick={() => navigate(section.path)}
                                            className="flex w-full items-center gap-3 py-3 text-right transition hover:bg-slate-50 sm:px-2"
                                        >
                                            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border ${section.softColor}`}>
                                                <Icon className="h-5 w-5" />
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="flex flex-wrap items-center gap-2">
                                                    <span className="truncate font-semibold">{displayName}</span>
                                                    <Badge variant="outline">{section.label}</Badge>
                                                    {item.quickRating && <Badge className="bg-blue-600">{item.quickRating}</Badge>}
                                                </span>
                                                <span className="mt-1 block text-xs text-slate-500">{formatPersianDateTime(item.createdAt)}</span>
                                            </span>
                                            <ArrowLeft className="h-4 w-4 shrink-0 text-slate-300" />
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </main>
        </div>
    );
};

export default AdminDashboard;
